import React, { useEffect } from 'react';
import { ListPlayer, ListPlayerContext } from 'react-list-player';
import { useAudio } from '../../contexts/AudioContext';
import styled from 'styled-components';

interface Track {
    title: { type: 'text'; content: string }[];
    artist: { type: 'text'; content: string }[];
    album: { type: 'text'; content: string }[];
    duration: string;
    src: string;
}

interface ListInfo {
    type: 'album';
    name: string;
    numTracks: number;
    duration: string;
    releaseDate?: string;
    genre?: string;
    imageSrc?: string;
}

interface AlbumPlaylistProps {
    albumId: string;
    tracks: Track[];
    listInfo: ListInfo;
}

const PlaylistWrapper = styled.div`
    width: 100%;
    height: 100%;
    position: relative;
    z-index: 20;

    /* Custom styling for react-list-player to match theme */
    .list-player {
        background: transparent;
        color: ${({ theme }) => theme.colors.text};
    }

    /* Fix text overflow in album info header - remove masks and allow wrapping */
    .list-player .lt-info-panel {
        overflow: visible !important;
    }

    .list-player h1.lt-info-title-h1,
    .list-player .lt-info-title h1 {
        white-space: normal !important;
        overflow: visible !important;
        text-overflow: clip !important;
        word-wrap: break-word !important;
    }

    .list-player h1.lt-info-title-h1 > span,
    .list-player .lt-info-stats > span {
        white-space: normal !important;
        overflow-wrap: break-word !important;
    }

    .list-player .lt-info-stats {
        flex-wrap: wrap !important;
        overflow: visible !important;
    }

    /* Remove the gradient masks that fade out text */
    .list-player .lt-info-panel,
    .list-player .main-info-cont,
    .list-player .info-cont {
        -webkit-mask-image: none !important;
        mask-image: none !important;
    }
`;

export const AlbumPlaylist: React.FC<AlbumPlaylistProps> = ({ albumId, tracks, listInfo }) => {
    const {
        currentPlaylist,
        setCurrentPlaylist,
        selectedTrack,
        setSelectedTrack,
        isPlaying,
        setIsPlaying,
        isMuted,
        setIsMuted,
        audioRef,
        savedPosition,
        setSavedPosition,
        setTotalTracks,
        setOnTrackEnd,
    } = useAudio();

    const handlePlay = async (trackIndex: number, resume: boolean) => {
        const isSameTrack = currentPlaylist === albumId && trackIndex === selectedTrack;

        // If clicking a track from another playlist, switch the active playlist
        if (currentPlaylist !== albumId) {
            setCurrentPlaylist(albumId);
            setSelectedTrack(trackIndex);
        } else if (trackIndex !== selectedTrack) {
            setSelectedTrack(trackIndex);
        }

        setIsPlaying(true);

        // Load and play the MP3
        if (audioRef.current && tracks[trackIndex]) {
            // If it's a different track, reset saved position
            if (!isSameTrack) {
                setSavedPosition(0);
                audioRef.current.src = tracks[trackIndex].src;
                audioRef.current.load();
            } else if (resume) {
                // Resuming the same track - restore saved position
                audioRef.current.currentTime = savedPosition;
            } else {
                // Starting the same track from beginning
                audioRef.current.currentTime = 0;
                setSavedPosition(0);
            }

            try {
                await audioRef.current.play();
            } catch (error) {
                // Handle play() interruption gracefully
                console.log('Play interrupted:', error);
            }
        }
    };

    const handlePause = () => {
        setIsPlaying(false);
        if (audioRef.current) {
            // Save current position before pausing
            setSavedPosition(audioRef.current.currentTime);
            audioRef.current.pause();
        }
    };

    const handleMute = () => {
        const newMutedState = !isMuted;
        setIsMuted(newMutedState);
        if (audioRef.current) {
            audioRef.current.muted = newMutedState;
        }
    };

    const handleTrackClick = (index: number) => {
        const isSameTrack = isActivePlaylist && index === selectedTrack;

        // If clicking the currently playing track, pause it
        if (isSameTrack && isPlaying) {
            handlePause();
        } else if (isSameTrack && !isPlaying) {
            // If clicking the same paused track, resume from saved position
            handlePlay(index, true);
        } else {
            // Different track, start from beginning
            handlePlay(index, false);
        }
    };

    // Register auto-advance callback when this playlist is active
    useEffect(() => {
        if (currentPlaylist === albumId) {
            setTotalTracks(tracks.length);
            setOnTrackEnd(() => () => {
                // Advance to next track if not at the end
                if (selectedTrack < tracks.length - 1) {
                    handlePlay(selectedTrack + 1, false);
                }
            });
        }

        // Cleanup when component unmounts or playlist changes
        return () => {
            if (currentPlaylist === albumId) {
                setOnTrackEnd(null);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPlaylist, albumId, selectedTrack, tracks.length]);

    // Determine if this playlist is the active one
    const isActivePlaylist = currentPlaylist === albumId;
    const activeTrack = isActivePlaylist ? selectedTrack : -1;
    const activeIsPlaying = isActivePlaylist ? isPlaying : false;

    return (
        <PlaylistWrapper>
            <ListPlayerContext.Provider
                value={{
                    selectedTrack: activeTrack,
                    setSelectedTrack: handleTrackClick,
                    isPlaying: activeIsPlaying,
                    setIsPlaying,
                    isMuted,
                    setIsMuted,
                }}
            >
                <ListPlayer
                    tracks={tracks}
                    listInfo={listInfo}
                    playCallback={(index: number, resume: boolean) => handlePlay(index, resume)}
                    pauseCallback={handlePause}
                    muteCallback={handleMute}
                />
            </ListPlayerContext.Provider>
        </PlaylistWrapper>
    );
};
